import { useCallback, useEffect, useRef, useState } from 'react';
import { listAdminTransactions, getAdminTransaction } from '@/lib/api/admin-transactions';
import { getAdminSubscription } from '@/lib/api/admin-subscriptions';
import { ApiError } from '@/lib/api/client';
import type {
  AdminTransactionListResponse,
  AdminTransactionDetail,
  ListAdminTransactionsParams,
} from '@/types/admin-transaction';
import type { AdminSubscription } from '@/types/admin-subscription';

/**
 * Spec 479 + 481 + #482: iterative ID-Suche (Kaskade) ueber alle Achsen — ein
 * Suchfeld, keine Typwahl. Owner-Reihenfolge (alle O(1)-Stufen vor allen
 * Scans), Stopp bei der ersten Stufe mit Treffern:
 *
 *   1. internetqTransactionId   (exakt, Facade-GSI, O(1))    — skip > 128 Zeichen
 *   2. Facade-TX-ID Detail-GET  (exakt, O(1))                — nur ^TXN_[A-Za-z0-9_]+$
 *   3. Subscription Detail-GET  (exakt, O(1))                — nur ^(SIQ|sub_)[a-zA-Z0-9_]+$
 *   4. customerReferenceExact   (exakt, Facade-GSI, O(1))    — skip > 100 Zeichen
 *   5. txId                     (Teilstring, BFF-Scan)       — skip > 24 Zeichen (Laengenbeweis)
 *
 * Stufe 2 umgeht die Facade-Listen-Luecke UND das Scan-Timeout: eine
 * vollstaendige TXN-ID haengt nicht an der Listen-Vollstaendigkeit. Stufe 3
 * (Spec 481) loest Subscription-IDs auf, die die Scan-Stufen prinzipiell nie
 * finden koennen (sie durchsuchen Transaktions-Felder).
 *
 * #482 (Facade v2.18 / deren #575): die Referenz-Stufe laeuft exakt und
 * case-sensitiv gegen den CustomerReference-GSI und steht damit VOR dem
 * txId-Scan. Der alte v2.17-Teilstring-Param `customerReference` bleibt als
 * Deep-Link-Achse lesbar (BFF reicht ihn weiter durch), ist aber KEINE
 * Kaskaden-Stufe mehr — er ist Scan-basiert und laeuft auf grossen Bestaenden
 * verlaesslich ins ~29-s-Gateway-Timeout (502).
 *
 * Fehlerpolitik (Spec D3): NUR ein sauberer Miss kaskadiert weiter (leere
 * Liste ohne `unavailable`; Detail-404/400). Alles andere — 502/503/504,
 * Netzwerkfehler, `unavailable`, unerwartete Status — bricht ab: ein Timeout
 * auf einer billigen Stufe prognostiziert ein schlimmeres auf den teuren.
 * Leer + `truncated` kaskadiert weiter, der Voll-Miss meldet das Teilergebnis.
 *
 * Der Hook orchestriert nur: er schreibt keine URL und seedet keinen Cache —
 * das macht die Seite mit dem Outcome (Trennung haelt die Logik unit-testbar).
 * Alle Requests laufen ohne Retry (HTTP-Schicht; die Query-Schicht ist nicht
 * beteiligt): listAdminTransactions schaltet bei aktiver Suche selbst auf
 * noRetry, Stufe 2 fordert es explizit an.
 */

/**
 * Achsen mit eigenem URL-Param. `customerReference` (v2.17-Teilstring) ist
 * seit #482 nur noch Deep-Link-Legacy — die Kaskade schreibt sie nie mehr.
 */
export type SearchAxis =
  | 'internetqTransactionId'
  | 'txId'
  | 'customerReference'
  | 'customerReferenceExact';

/** Alle Stufen, inkl. der param-losen Punkt-Lookups (Fortschrittsanzeige). */
export type CascadeStage = SearchAxis | 'transactionDetail' | 'subscriptionDetail';

export type CascadeOutcome =
  /** Listen-Treffer: `params` sind exakt die Seiten-Query-Params nach dem URL-Schreiben (Cache-Seed-Key). */
  | {
      type: 'hit';
      axis: SearchAxis;
      response: AdminTransactionListResponse;
      params: ListAdminTransactionsParams;
    }
  /** Stufe-2-Treffer: die Seite navigiert ins Detail (Spec D4). */
  | { type: 'detailHit'; detail: AdminTransactionDetail }
  /** Stufe-3-Treffer (Spec 481): die Seite navigiert auf die per subId gefilterte Subscriptions-Uebersicht. */
  | { type: 'subscriptionHit'; detail: AdminSubscription }
  /** Alle Stufen trefferlos durchlaufen. */
  | { type: 'miss'; sawTruncated: boolean }
  /** Infrastruktur-Abbruch (Stop-on-Error, Spec D3). */
  | { type: 'error'; stage: CascadeStage }
  /** Discard-Guard: Filter-Drift/Unmount waehrend des Laufs — nichts anwenden. */
  | { type: 'discarded' };

export interface CascadeRunOptions {
  /** Aktive Server-Filter OHNE die drei Such-Params, inkl. `limit` (Cache-Key-Kongruenz, Spec D6). */
  baseFilters: ListAdminTransactionsParams;
  /**
   * Gesetzter Merchant-Filter fuer die Punkt-Lookups (Platform-Rollen, Spec
   * D7): Stufe 2 reicht ihn als BFF-Hint durch, Stufe 3 (Spec 481 D6) prueft
   * ihn client-seitig gegen den Response (der Sub-Endpunkt kennt keinen Hint).
   */
  merchantScopeHint?: string;
  /**
   * Discard-Guard (Spec D6): muss false liefern, sobald sich der Server-
   * Filterzustand seit dem Submit geaendert hat (Back/Forward, externe
   * URL-Mutation) — das Outcome wird dann verworfen statt angewendet.
   */
  isStillValid: () => boolean;
}

/**
 * Stufe-2-Gate: bewusst lax — auch partielle Begriffe wie `TXN_MR4V` passieren
 * (Detail-404 → txId-Scan findet den Teilstring). Bewusst NUR `TXN_` (keine
 * lokalen `tx_`-IDs — die existieren im Facade-only-Betrieb nicht, Spec D2).
 */
export const FULL_TXN_ID_PATTERN = /^TXN_[A-Za-z0-9_]+$/;

/**
 * Stufe-3-Gate (Spec 481 D2): das kanonische Subscription-ID-Pattern aus #476
 * (im BFF am Terminate-Pfad erzwungen, im Webhook-Schema verankert; der
 * Detail-GET delegiert die Format-Validierung an die Facade). Format-Paritaet:
 * ein Begriff ausserhalb des Patterns kann keine Subscription-ID sein. `sub_`
 * bleibt drin (Paritaet), obwohl facade-only praktisch nur SIQ* existiert —
 * ein sub_-Begriff endet als billiger 404-Miss (tx_-Analogie).
 */
export const FULL_SUBSCRIPTION_ID_PATTERN = /^(SIQ|sub_)[a-zA-Z0-9_]+$/;

/** Contract-Grenzen (Spec D2): Stufen-Gates statt BFF-400s aus der Kaskade. */
export const IQ_TX_ID_MAX_LENGTH = 128;
/** #482: Contract-v2.18-Grenze fuer `customerReferenceExact` (maxLength 100). */
export const CUSTOMER_REFERENCE_EXACT_MAX_LENGTH = 100;

/**
 * txId-Scan-Laengen-Gate (Spec 481 D9, Owner-Fund 2): Facade-TX-IDs tragen das
 * Fixformat `TXN_` + 8 + `_` + 8 = 21 Zeichen (Contract-v2.17-Beispiele, alle
 * Live-Bestaende; der Contract pinnt fuer TransactionID weder Pattern noch
 * Laenge — daher die konservative Owner-Marge 24 statt exakt 21). Ein Begriff,
 * der laenger ist als die laengste existierende transactionId, kann nie ein
 * Teilstring davon sein — der Scan waere garantiert trefferlos und wuerde nur
 * das 29-s-Gate verbrennen, bevor die Referenz-Stufe drankommt. Einziger
 * Anpassungspunkt, falls die Facade das TXN-Format je verlaengert.
 */
export const TXN_ID_SCAN_MAX_TERM_LENGTH = 24;

export interface UseTransactionSearchCascade {
  /** Laufende Stufe (Fortschrittsanzeige) oder null. */
  runningStage: CascadeStage | null;
  isRunning: boolean;
  /** Kaskade ausfuehren; `term` wird getrimmt. Resolved immer (nie reject). */
  run: (term: string, opts: CascadeRunOptions) => Promise<CascadeOutcome>;
}

export function useTransactionSearchCascade(): UseTransactionSearchCascade {
  const [runningStage, setRunningStage] = useState<CascadeStage | null>(null);
  const mountedRef = useRef(true);
  const runningRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setStage = useCallback((stage: CascadeStage | null) => {
    if (mountedRef.current) setRunningStage(stage);
  }, []);

  const run = useCallback(
    async (rawTerm: string, opts: CascadeRunOptions): Promise<CascadeOutcome> => {
      const term = rawTerm.trim();
      // Doppel-Submit-Schutz (die Seite disabled den Button zusaetzlich).
      if (!term || runningRef.current) return { type: 'discarded' };
      runningRef.current = true;

      const guard = (): boolean => mountedRef.current && opts.isStillValid();
      let sawTruncated = false;

      // Stufen 1/3/4: identische Listen-Mechanik, nur die Achse wechselt.
      const listStage = async (axis: SearchAxis): Promise<CascadeOutcome | 'continue'> => {
        setStage(axis);
        const params: ListAdminTransactionsParams = {
          ...opts.baseFilters,
          [axis]: term,
          cursor: undefined,
        };
        let response: AdminTransactionListResponse;
        try {
          response = await listAdminTransactions(params);
        } catch {
          // 502/503/504, Netzwerkfehler, unerwartete Status: Stop-on-Error.
          return { type: 'error', stage: axis };
        }
        if (response.unavailable) return { type: 'error', stage: axis };
        if ((response.data?.length ?? 0) > 0) return { type: 'hit', axis, response, params };
        if (response.truncated) sawTruncated = true;
        return 'continue';
      };

      const detailStage = async (): Promise<CascadeOutcome | 'continue'> => {
        setStage('transactionDetail');
        try {
          const detail = await getAdminTransaction(term, opts.merchantScopeHint, {
            noRetry: true,
          });
          return { type: 'detailHit', detail };
        } catch (err) {
          // Sauberer Miss: 404 (existiert nicht / fremder Tenant — nicht
          // unterscheidbar, fail-closed) und 400 (Format, durchs Gate
          // praktisch unerreichbar) kaskadieren weiter.
          if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
            return 'continue';
          }
          return { type: 'error', stage: 'transactionDetail' };
        }
      };

      // Spec 481: Subscription-Voll-ID als O(1)-Punkt-Lookup — Miss-/Fehler-
      // Politik identisch zu Stufe 2. Der BFF-Detail-Endpunkt kennt keinen
      // merchantId-Hint; die Merchant-Scope-Spiegelung (Spec D6) laeuft daher
      // client-seitig gegen den Response — Verhaltens-Konsistenz zu Stufe 2,
      // KEINE Sicherheitsgrenze (Merchant-Tenancy erzwingt der BFF fail-closed
      // per 404, Platform-Rollen duerfen jede Subscription sehen).
      const subscriptionDetailStage = async (): Promise<CascadeOutcome | 'continue'> => {
        setStage('subscriptionDetail');
        try {
          const detail = await getAdminSubscription(term, { noRetry: true });
          if (opts.merchantScopeHint && detail.merchantId !== opts.merchantScopeHint) {
            return 'continue';
          }
          return { type: 'subscriptionHit', detail };
        } catch (err) {
          if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
            return 'continue';
          }
          return { type: 'error', stage: 'subscriptionDetail' };
        }
      };

      try {
        const stages: Array<() => Promise<CascadeOutcome | 'continue'>> = [];
        if (term.length <= IQ_TX_ID_MAX_LENGTH) {
          stages.push(() => listStage('internetqTransactionId'));
        }
        if (FULL_TXN_ID_PATTERN.test(term)) {
          stages.push(detailStage);
        }
        // Spec 481: alle O(1)-Stufen vor allen Scans (Owner-Vorgabe 5).
        if (FULL_SUBSCRIPTION_ID_PATTERN.test(term)) {
          stages.push(subscriptionDetailStage);
        }
        // #482: Referenz-Stufe exakt via v2.18-GSI (O(1)) — nach demselben
        // Ordnungsprinzip VOR dem txId-Scan. Der v2.17-Teilstring-Fallback
        // entfaellt ersatzlos (Scan, 502-Timeout auf grossen Bestaenden).
        if (term.length <= CUSTOMER_REFERENCE_EXACT_MAX_LENGTH) {
          stages.push(() => listStage('customerReferenceExact'));
        }
        // Spec 481 D9 (Laengenbeweis): laenger als die laengste TXN-ID ⇒ nie
        // Teilstring ⇒ der Scan wuerde nur das 29-s-Gate verbrennen.
        if (term.length <= TXN_ID_SCAN_MAX_TERM_LENGTH) {
          stages.push(() => listStage('txId'));
        }

        if (!guard()) return { type: 'discarded' };
        for (const stage of stages) {
          const result = await stage();
          // Drift/Unmount schlaegt jedes Stufen-Ergebnis (auch Treffer/Fehler):
          // ein Outcome gegen veraltete Filter darf nie angewendet werden.
          if (!guard()) return { type: 'discarded' };
          if (result !== 'continue') return result;
        }
        return { type: 'miss', sawTruncated };
      } finally {
        runningRef.current = false;
        setStage(null);
      }
    },
    [setStage],
  );

  return { runningStage, isRunning: runningStage !== null, run };
}
